import {
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { z } from 'zod';
import { DeepNullablePartial } from '../../utils';
import { SelectOption } from '../forms/dropdown-select-input';
import {
  InteractiveQuoteLoanCategory,
  InteractiveRatesRateFragmentFragment,
  LoanTerm,
  OccupancyType,
  ResidenceType,
  SortDirection,
  SortField,
  TransactionPurpose,
  TransactionPurposeTab,
  WidgetInteractiveRatesFormValuesFragmentFragment,
  WidgetInteractiveRatesSliderRangesFragmentFragment,
} from '../RatesTool/types';

export interface FieldConfig<T extends FieldValues> {
  field: FieldPath<T>;
}
export interface FormConfig<T extends FieldValues> {
  topLine: FieldPath<T>[] | FieldConfig<T>[];
  expandedLines: FieldPath<T>[][] | FieldConfig<T>[][];
}

export const refiOptions: { label: string; value: TransactionPurpose }[] = [
  { label: 'Take cash out', value: TransactionPurpose.RefiCashOut },
  { label: 'Lower payment', value: TransactionPurpose.RefiLowerPayment },
  { label: 'Change terms', value: TransactionPurpose.RefiChangeTerms },
];

export const tabOptions = [
  { label: 'Purchase', value: TransactionPurposeTab.Purchase },
  { label: 'Refinance', value: TransactionPurposeTab.Refinance },
  { label: 'HELOC', value: TransactionPurposeTab.HELOC },
] as const;

export type InteractiveRatesFormShape = z.infer<
  typeof interactiveRatesFormValidationSchema
>;

export type FilterDefaultsShape = {
  principalAndInterestIsDefault: boolean;
  equityIsDefault: boolean;
  closingCostsIsDefault: boolean;
  interestRateIsDefault: boolean;
  loanCostsIsDefault: boolean;
};

export interface InteractiveRatesFormProps {
  ficoOptions: SelectOption[];
  loanTypeOptions: SelectOption[];
  loanTermOptions: SelectOption[];
  occupancyTypeOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  sortFieldOptions: SelectOption[];
  defaultValues: DeepNullablePartial<InteractiveRatesFormShape>;
  fieldErrors?: FieldErrors<InteractiveRatesFormShape>;
  onSubmit: (values: InteractiveRatesFormShape) => void;
  loading: boolean;
  sliderRanges?: WidgetInteractiveRatesSliderRangesFragmentFragment;
  selectedPurposeTab: TransactionPurposeTab;
  setSelectedPurposeTab: React.Dispatch<
    React.SetStateAction<TransactionPurposeTab>
  >;
  selectedGoalTab: TransactionPurpose;
  setSelectedGoalTab: React.Dispatch<React.SetStateAction<TransactionPurpose>>;
  rates?: InteractiveRatesRateFragmentFragment[] | undefined;
  queryIsLoading: boolean;
  ctaLink: string;
  isUpdating: boolean;
  filterDefaults: FilterDefaultsShape;
}

export const interactiveRatesFormValidationSchema = z.object({
  selectedLoanTerms: z.array(z.nativeEnum(LoanTerm)),
  selectedIqLoanCategories: z.array(z.nativeEnum(InteractiveQuoteLoanCategory)),
  transactionPurpose: z.nativeEnum(TransactionPurpose).optional(),
  purchasePrice: z.number().min(0),
  downPayment: z.number().min(0).nullish(),
  propertyValue: z.number().min(0),
  remainingLoanBalance: z.number().min(0),
  cashOutAmount: z.number().min(0),
  propertyZip: z.string().min(0),
  ficoScore: z.number().optional(),
  annualIncome: z.number().optional().nullish(),
  monthlyDebt: z.number().optional().nullish(),
  occupancyType: z.nativeEnum(OccupancyType).optional(),
  residenceType: z.nativeEnum(ResidenceType).optional(),
  numUnits: z.number().optional(),
  totalAssets: z.number().min(0),
  isTargetCashToClose: z.boolean().optional(),

  principalAndInterestMax: z.number().optional(),
  principalAndInterestMin: z.number().optional(),
  equityMax: z.number().optional(),
  equityMin: z.number().optional(),
  interestRateMax: z.number().optional(),
  interestRateMin: z.number().optional(),
  closingCostsMax: z.number().optional(),
  closingCostsMin: z.number().optional(),
  loanCostsMax: z.number().optional(),
  loanCostsMin: z.number().optional(),

  monthlyPropertyTaxes: z.number().optional().nullish(),
  monthlyHoi: z.number().optional().nullish(),
  monthlyHoa: z.number().optional().nullish(),
  sellerCredits: z.number().optional().nullish(),
  earnestMoneyDeposit: z.number().optional().nullish(),
  isEscrowWaived: z.boolean().optional(),

  sortDir: z.nativeEnum(SortDirection),
  sortField: z.nativeEnum(SortField),
  clearSettings: z.boolean().optional(),
  clearFilters: z.boolean().optional(),
});

export const purchaseFormConfig: FormConfig<InteractiveRatesFormShape> = {
  topLine: [
    'purchasePrice',
    'downPayment',
    'propertyZip',
    'ficoScore',
    'annualIncome',
  ],
  expandedLines: [
    [
      'monthlyDebt',
      'totalAssets',
      'occupancyType',
      'residenceType',
      'numUnits',
    ],
    [
      'monthlyPropertyTaxes',
      'monthlyHoi',
      'monthlyHoa',
      'sellerCredits',
      'earnestMoneyDeposit',
      'isEscrowWaived',
    ],
  ],
};

export const cashOutRefinanceFormConfig: FormConfig<InteractiveRatesFormShape> =
  {
    topLine: [
      'transactionPurpose',
      'propertyValue',
      'remainingLoanBalance',
      'propertyZip',
      'cashOutAmount',
    ],
    expandedLines: [
      [
        'ficoScore',
        'annualIncome',
        'monthlyDebt',
        'occupancyType',
        'residenceType',
        'numUnits',
      ],
      ['monthlyPropertyTaxes', 'monthlyHoi', 'monthlyHoa', 'isEscrowWaived'],
    ],
  };

export const rateTermRefinanceFormConfig: FormConfig<InteractiveRatesFormShape> =
  {
    topLine: [
      'transactionPurpose',
      'propertyValue',
      'remainingLoanBalance',
      'annualIncome',
      'propertyZip',
    ],
    expandedLines: [
      [
        'ficoScore',
        'totalAssets',
        'monthlyDebt',
        'occupancyType',
        'residenceType',
        'numUnits',
      ],
      [
        'isTargetCashToClose',
        'monthlyPropertyTaxes',
        'monthlyHoi',
        'monthlyHoa',
        'isEscrowWaived',
      ],
    ],
  };

export function useSliderRanges(
  methods: UseFormReturn<InteractiveRatesFormShape>,
  sliderRanges?: WidgetInteractiveRatesSliderRangesFragmentFragment
) {
  return {
    interestRate: {
      min:
        methods.getValues('interestRateMin') ??
        sliderRanges?.interestRatesRange[0],
      max:
        methods.getValues('interestRateMax') ??
        sliderRanges?.interestRatesRange[1],
    },
    principalAndInterest: {
      min:
        methods.getValues('principalAndInterestMin') ??
        sliderRanges?.principalAndInterestRange[0],
      max:
        methods.getValues('principalAndInterestMax') ??
        sliderRanges?.principalAndInterestRange[1],
    },
    totalLoanCosts: {
      min:
        methods.getValues('loanCostsMin') ??
        sliderRanges?.totalLoanCostsRange[0],
      max:
        methods.getValues('loanCostsMax') ??
        sliderRanges?.totalLoanCostsRange[1],
    },
    equity: {
      min: methods.getValues('equityMin') ?? sliderRanges?.equityRange[0],
      max: methods.getValues('equityMax') ?? sliderRanges?.equityRange[1],
    },
    closingCosts: {
      min:
        methods.getValues('closingCostsMin') ??
        sliderRanges?.closingCostsRange[0],
      max:
        methods.getValues('closingCostsMax') ??
        sliderRanges?.closingCostsRange[1],
    },
  };
}

export function dropdownSelectLabelClassName(
  value: undefined | number | number[] | string | string[] | null | any[],
  options: SelectOption[]
): string {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    // No options selected
    return 'bg-hardwood-100 text-hardwood-600';
  }

  // Convert `value` to an array if it's a single item
  const selectedValues = Array.isArray(value) ? value : [value];

  // Check if all options are selected or if selected values contain all options
  const allOptionsSelected = options.every((option) =>
    selectedValues.includes(option.value)
  );

  return allOptionsSelected
    ? 'bg-hardwood-100 text-hardwood-600'
    : 'bg-morton-300 text-morton-700';
}

export function convertInteractiveRatesFormShapeToFormValues(
  formShape: InteractiveRatesFormShape
): WidgetInteractiveRatesFormValuesFragmentFragment {
  const {
    annualIncome,
    downPayment,
    ficoScore,
    selectedIqLoanCategories,
    selectedLoanTerms,
    monthlyDebt,
    numUnits,
    occupancyType,
    propertyZip,
    purchasePrice,
    residenceType,
    cashOutAmount,
    propertyValue,
    remainingLoanBalance,
    totalAssets,
    isTargetCashToClose,
    transactionPurpose,
    sortDir,
    sortField,
    closingCostsMax,
    closingCostsMin,
    equityMax,
    equityMin,
    interestRateMax,
    interestRateMin,
    loanCostsMax,
    loanCostsMin,
    principalAndInterestMax,
    principalAndInterestMin,
    monthlyPropertyTaxes,
    monthlyHoi,
    monthlyHoa,
    sellerCredits,
    earnestMoneyDeposit,
    isEscrowWaived,
    clearSettings,
    clearFilters,
  } = formShape;

  return {
    closingCostsMax,
    closingCostsMin,
    equityMax,
    equityMin,
    interestRateMax,
    interestRateMin,
    loanCostsMax,
    loanCostsMin,
    principalAndInterestMax,
    principalAndInterestMin,
    cashOutAmount,
    propertyValue,
    remainingLoanBalance,
    totalAssets,
    isTargetCashToClose,
    transactionPurpose,
    selectedIqLoanCategories,
    selectedLoanTerms,
    annualIncome,
    downPayment,
    ficoScore,
    monthlyDebt,
    numUnits,
    occupancyType,
    propertyZip,
    purchasePrice,
    residenceType,
    monthlyPropertyTaxes,
    monthlyHoi,
    monthlyHoa,
    sellerCredits,
    earnestMoneyDeposit,
    isEscrowWaived,
    sortDir: sortDir || SortDirection.Asc,
    sortField,
    clearSettings,
    clearFilters,
  };
}

export function convertFormValuesToInteractiveRatesFormShape(
  formValues?: WidgetInteractiveRatesFormValuesFragmentFragment
): DeepNullablePartial<InteractiveRatesFormShape> {
  const {
    annualIncome,
    downPayment,
    ficoScore,
    monthlyDebt,
    numUnits,
    occupancyType,
    propertyZip,
    purchasePrice,
    residenceType,
    selectedIqLoanCategories,
    selectedLoanTerms,
    cashOutAmount,
    propertyValue,
    remainingLoanBalance,
    totalAssets,
    transactionPurpose,
    isTargetCashToClose,
    sortDir,
    sortField,
    closingCostsMax,
    closingCostsMin,
    equityMax,
    equityMin,
    interestRateMax,
    interestRateMin,
    loanCostsMax,
    loanCostsMin,
    principalAndInterestMax,
    principalAndInterestMin,
    monthlyPropertyTaxes,
    monthlyHoi,
    monthlyHoa,
    sellerCredits,
    earnestMoneyDeposit,
    isEscrowWaived,
    clearSettings,
    clearFilters,
  } = formValues || {};

  return {
    closingCostsMax,
    closingCostsMin,
    equityMax,
    equityMin,
    interestRateMax,
    interestRateMin,
    loanCostsMax,
    loanCostsMin,
    principalAndInterestMax,
    principalAndInterestMin,
    annualIncome,
    downPayment,
    cashOutAmount,
    propertyValue,
    remainingLoanBalance,
    isTargetCashToClose,
    totalAssets,
    transactionPurpose,
    selectedLoanTerms,
    selectedIqLoanCategories,
    propertyZip,
    purchasePrice,
    ficoScore,
    monthlyDebt,
    numUnits,
    occupancyType,
    residenceType,
    monthlyPropertyTaxes,
    monthlyHoi,
    monthlyHoa,
    sellerCredits,
    earnestMoneyDeposit,
    isEscrowWaived,
    clearSettings,
    clearFilters,
    sortDir,
    sortField,
  };
}

export const getTransactionPurpose = (tab: TransactionPurposeTab) => {
  switch (tab) {
    case TransactionPurposeTab.Purchase:
      return TransactionPurpose.Purchase;
    case TransactionPurposeTab.Refinance:
      return TransactionPurpose.RefiCashOut;
    case TransactionPurposeTab.HELOC:
      return TransactionPurpose.RefiHeloc;
    default:
      return TransactionPurpose.Purchase;
  }
};

export const getTransactionPurposeTab = (purpose: TransactionPurpose) => {
  switch (purpose) {
    case TransactionPurpose.RefiCashOut:
    case TransactionPurpose.RefiChangeTerms:
    case TransactionPurpose.RefiLowerPayment:
      return TransactionPurposeTab.Refinance;
    case TransactionPurpose.RefiHeloc:
      return TransactionPurposeTab.HELOC;
    case TransactionPurpose.Purchase:
      return TransactionPurposeTab.Purchase;
    default:
      return TransactionPurposeTab.Purchase;
  }
};
